import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Paper, TextField, IconButton, Tooltip, Popover, Typography, RadioGroup, FormControlLabel, Radio, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Chip, DialogTitle, DialogContent } from "@mui/material";
import AppDialog from "../../../common/AppDialog";
import { FileDownload as FileDownloadIcon, PictureAsPdf as PdfIcon, TableChart as ExcelIcon, Visibility as VisibilityIcon, Close as CloseIcon, } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import Loader from "../../../common/spinner";
import { getStatusColor } from "../../../utils/statusColor";
import AddEmployee from "./addEmployee";
import ImportEmployeesCSV from "./importEmployeesCSV";

function EmployeeManagement() {
    const navigate = useNavigate();
    const userDataRaw = localStorage.getItem("userData");
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const orgId = userData?.organization_id;

    const [employees, setEmployees] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [downloadFormat, setDownloadFormat] = useState("");
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openImportDialog, setOpenImportDialog] = useState(false);
    const [state, setState] = useState({
        page: 0,
        limit: 10,
        totalCount: 0,
        searchQuery: "",
        filterStatus: "All",
        selectedLocation: "All",
    });

    const updateState = (newState) =>
        setState((prev) => ({ ...prev, ...newState }));

    const fetchLocations = useCallback(async (id) => {
        try {
            const response = await getData(
                API_ENDPOINTS.fetchLocationsByOrganizationId(id),
            );
            if (response?.status === "SUCCESS" && Array.isArray(response.message)) {
                setLocations(
                    response.message.map((loc) => ({
                        id: loc.id,
                        name: loc.location_name,
                    })),
                );
            }
        } catch (error) {
            return null;
        }
    }, []);

    const fetchEmployees = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            let url = API_ENDPOINTS.fetchEmployeesByOrganizationId(orgId);

            if (state.selectedLocation && state.selectedLocation !== "All") {
                url += `&location=${encodeURIComponent(state.selectedLocation)}`;
            }
            if (state.filterStatus && state.filterStatus !== "All") {
                url += `&status=${state.filterStatus}`;
            }

            const response = await getData(url);

            if (response?.status === "SUCCESS") {
                const data = response.message.employees || [];
                setEmployees(data);
                updateState({ totalCount: data.length });
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [orgId, state.selectedLocation, state.filterStatus]);

    const filteredEmployees = employees.filter((emp) => {
        const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
        const empId = (emp.emp_id || "").toString().toLowerCase();
        const search = state.searchQuery.toLowerCase();

        const matchesSearch = fullName.includes(search) || empId.includes(search);

        const matchesStatus =
            state.filterStatus === "All" ||
            (emp.status &&
                emp.status.toLowerCase() === state.filterStatus.toLowerCase());

        const matchesLocation =
            state.selectedLocation === "All" ||
            emp.location === state.selectedLocation;

        return matchesSearch && matchesStatus && matchesLocation;
    });

    useEffect(() => {
        if (orgId) {
            fetchLocations(orgId);
        }
    }, [orgId, fetchLocations]);

    useEffect(() => {
        if (orgId) {
            fetchEmployees();
        }
    }, [orgId, fetchEmployees]);

    const handleDownloadClick = (event) => setAnchorEl(event.currentTarget);
    const handleDownloadClose = () => {
        setAnchorEl(null);
        setDownloadFormat("");
    };

    const startDownload = async () => {
        if (!orgId || !downloadFormat) return;

        const status = state.filterStatus;
        const locationObj = locations.find(loc => loc.name === state.selectedLocation);
        const locationId = locationObj ? locationObj.id : "All";

        try {
            let endpoint = "";
            if (downloadFormat === "pdf") {
                endpoint = API_ENDPOINTS.downloadEmployeeListPDF(orgId, status, locationId);
            } else if (downloadFormat === "excel") {
                endpoint = API_ENDPOINTS.downloadEmployeeListExcel(orgId, status, locationId);
            }

            const response = await getData(endpoint, { responseType: 'blob' });

            const blob = new Blob([response], {
                type: downloadFormat === "pdf"
                    ? "application/pdf"
                    : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Employee_List_${new Date().getTime()}.${downloadFormat === "pdf" ? "pdf" : "xlsx"}`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            return null;
        } finally {
            handleDownloadClose();
        }
    };

    const openDownload = Boolean(anchorEl);
    const handleOpenAddDialog = () => setOpenAddDialog(true);
    const handleCloseAddDialog = () => setOpenAddDialog(false);
    const handleOpenImportDialog = () => setOpenImportDialog(true);
    const handleCloseImportDialog = () => setOpenImportDialog(false);

    if (loading && employees.length === 0) return <Loader />;

    return (
        <Box sx={{ p: 1 }}>
            <Typography variant="h5" fontWeight="700" sx={{ mb: 2 }}>
                Employee Management System
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                        fullWidth
                        label="Search Name or ID"
                        variant="outlined"
                        value={state.searchQuery}
                        size="small"
                        onChange={(e) =>
                            updateState({ searchQuery: e.target.value, page: 0 })
                        }
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Branch</InputLabel>
                        <Select
                            value={state.selectedLocation}
                            label="Branch"
                            onChange={(e) =>
                                updateState({ selectedLocation: e.target.value, page: 0 })
                            }
                        >
                            <MenuItem value="All">All</MenuItem>
                            {locations.map((item) => (
                                <MenuItem key={item.id} value={item.name}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={state.filterStatus}
                            onChange={(e) =>
                                updateState({ filterStatus: e.target.value, page: 0 })
                            }
                            label="Status"
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 2 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        size="medium"
                        sx={{ textTransform: "none" }}
                        onClick={handleOpenAddDialog}
                    >
                        Add Employee
                    </Button>
                </Grid>
                <Grid size={{ xs: 6, sm: 6, md: 2 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        size="medium"
                        sx={{ textTransform: "none" }}
                        onClick={handleOpenImportDialog}
                    >
                        Import Excel
                    </Button>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 1 }} sx={{ textAlign: "center" }}>
                    <Tooltip title="Download List">
                        <IconButton
                            onClick={handleDownloadClick}
                            sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}
                        >
                            <FileDownloadIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Emp Id</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Name</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Department</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Team</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Designation</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Status</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }} align="center">
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                            {filteredEmployees
                                .slice(
                                    state.page * state.limit,
                                    state.page * state.limit + state.limit,
                                )
                                .map((emp) => {
                                    const normalizedStatus = emp.status
                                        ? emp.status.charAt(0).toUpperCase() +
                                        emp.status.slice(1).toLowerCase()
                                        : "";
                                    const styles = getStatusColor(normalizedStatus);

                                    return (
                                        <TableRow key={emp.id} hover sx={{
                                            '&:hover': {
                                                bgcolor: '#F1F5F9 !important'
                                            }
                                        }}>
                                            <TableCell sx={{ color: '#1E293B' }}>{emp.emp_id || "N/A"}</TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>{`${emp.first_name} ${emp.last_name}`}</TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>{emp.department || "N/A"}</TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>{emp.team || "N/A"}</TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>{emp.designation || "N/A"}</TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                <Chip
                                                    label={emp.status}
                                                    size="small"
                                                    sx={{
                                                        width: "90px",
                                                        backgroundColor: styles.bg,
                                                        color: styles.color,
                                                        fontWeight: "600",
                                                        textTransform: "capitalize",
                                                        border: `1px solid ${styles.color}20`,
                                                        borderRadius: "24px",
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: '#1E293B' }} align="center">
                                                <IconButton size="small" onClick={() => {
                                                    navigate(`/hr/employee-details?employeeId=${emp.id}`);
                                                }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {filteredEmployees.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        No employees found matching your criteria.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredEmployees.length}
                page={state.page}
                onPageChange={(_, newPage) => updateState({ page: newPage })}
                rowsPerPage={state.limit}
                onRowsPerPageChange={(event) =>
                    updateState({
                        limit: parseInt(event.target.value, 10),
                        page: 0,
                    })
                }
                rowsPerPageOptions={[5, 10, 25]}
            />

            <AppDialog
                open={openAddDialog}
                onClose={handleCloseAddDialog}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                    },
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, fontWeight: 700 }}>
                    Add New Employee
                    <IconButton
                        onClick={handleCloseAddDialog}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <AddEmployee
                        onClose={handleCloseAddDialog}
                        refreshData={fetchEmployees}
                    />
                </DialogContent>
            </AppDialog>

            <AppDialog
                open={openImportDialog}
                onClose={handleCloseImportDialog}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: "12px" } }}
            >
                <DialogTitle
                    sx={{
                        m: 0,
                        p: 2,
                        fontWeight: 700,
                        bgcolor: "#1976d2",
                        color: "#fff",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    Add Employee From CSV
                    <IconButton onClick={handleCloseImportDialog} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <ImportEmployeesCSV onClose={handleCloseImportDialog} />
                </DialogContent>
            </AppDialog>

            <Popover
                open={openDownload}
                anchorEl={anchorEl}
                onClose={handleDownloadClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: { p: 2, width: 220, borderRadius: 2 } }}
            >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Export Format
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <RadioGroup
                    value={downloadFormat}
                    onChange={(e) => setDownloadFormat(e.target.value)}
                >
                    <FormControlLabel
                        value="pdf"
                        control={<Radio size="small" />}
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <PdfIcon sx={{ color: "#ef4444", fontSize: 20 }} /> PDF
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value="excel"
                        control={<Radio size="small" />}
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <ExcelIcon sx={{ color: "#10b981", fontSize: 20 }} /> Excel
                            </Box>
                        }
                    />
                </RadioGroup>
                {downloadFormat && (
                    <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={startDownload}
                        sx={{ mt: 2 }}
                        startIcon={<FileDownloadIcon />}
                    >
                        Download {downloadFormat.toUpperCase()}
                    </Button>
                )}
            </Popover>
        </Box>
    );
}

export default EmployeeManagement;